import { isNone } from '@ember/utils';
import EmberObject, { get, set, computed } from '@ember/object';
import { underscore } from '@ember/string';
import { copy } from 'ember-copy';
import { A, isArray } from '@ember/array';
import { defer } from 'rsvp';

// TODO: Remove this hack copied over from dashboard.
function appContainer() {
  return window.Dashboard.__container__;
}
const builtInDatatypes = {};
builtInDatatypes[Date] = {
  deserialize(string) {
    if (!string) {
      return null;
    }
    return new Date(string);
  },
  serialize(date) {
    if (!date) {
      return null;
    }
    return date.toISOString();
  }
};
builtInDatatypes[Number] = {
  deserialize(string) {
    if (!string && string !== 0) {
      return null;
    }
    return Number(string);
  },
  serialize(number) {
    if (!number && number !== 0) {
      return null;
    }
    return Number(number);
  }
};
function modelForTypeString(typePath) {
  return appContainer().owner.factoryFor(`model:${typePath}`).class;
}
function deserialize(value, type) {
  if (typeof type === 'string') {
    type = modelForTypeString(type); // eslint-disable-line no-param-reassign
  }
  if (type && type.deserialize) {
    return type.deserialize(value);
  } else if (type && builtInDatatypes[type]) {
    return builtInDatatypes[type].deserialize(value);
  } else {
    return value;
  }
}
function serialize(value, type) {
  if (typeof type === 'string') {
    type = modelForTypeString(type); // eslint-disable-line no-param-reassign
  }
  if (type && type.serialize) {
    return type.serialize(value);
  } else if (type && builtInDatatypes[type]) {
    return builtInDatatypes[type].serialize(value);
  } else {
    return value;
  }
}
function getPropertyValue(property) {
  if (property && property.getAllProperties) {
    return property.getAllProperties();
  } else {
    return property;
  }
}
export function loadPromise(target) {
  if (isNone(target)) {
    return null;
  } else if (target.then) {
    return target;
  } else {
    const deferred = defer();

    // eslint-disable-next-line ember/no-get
    if (get(target, 'isLoaded') && !get(target, 'isNew')) {
      deferred.resolve(target);
    } else {
      target.one('didLoad', function () {
        deferred.resolve(target);
      });
    }
    return deferred.promise;
  }
}
export function repeated(type, options) {
  return attr({
    serialize(deserializedArray) {
      if (!deserializedArray) {
        return undefined;
      }
      return deserializedArray.map(deserializedObject => serialize(deserializedObject, type));
    },
    deserialize(serializedArray) {
      if (!serializedArray) {
        return undefined;
      }
      return A(serializedArray.map(serializedObject => deserialize(serializedObject, type)));
    }
  }, options);
}

/* NB: this function is using computed and `meta` values that may be
   deprecated and removed by Ember in either 4.0 or 5.0 (https://github.com/emberjs/rfcs/issues/536).
   However, Ember Data also uses these functions for the same purpose
   (see https://github.com/emberjs/data/blob/v3.13.1/packages/model/addon/-private/attr.js#L114).
   We will use Ember Data as a guide for how to remove these deprecated obejcts as needed. */
export function attr(type, options) {
  return computed('_data', {
    get(key) {
      const data = this._data;
      const dataKey = this.dataKey(key);
      const dataValue = data && get(data, dataKey);
      if (isNone(dataValue) && options && !isNone(options.defaultValue)) {
        if (typeof options.defaultValue === 'function') {
          return options.defaultValue();
        }
        return copy(options.defaultValue);
      }
      return deserialize(dataValue, type);
    },
    set(key, value) {
      const data = this._data;
      const dataKey = this.dataKey(key);
      const dataValue = data && get(data, dataKey);
      let dirtyAttributes = this._dirtyAttributes;
      let createdDirtyAttributes = false;
      let isDirty;
      if (!dirtyAttributes) {
        dirtyAttributes = A([]);
        createdDirtyAttributes = true;
      }
      const newValue = serialize(value, type);
      if (dataValue === undefined && options && newValue === options.defaultValue) {
        isDirty = false;
      } else if (typeof newValue === 'object') {
        isDirty = JSON.stringify(dataValue) !== JSON.stringify(newValue);
      } else {
        isDirty = dataValue !== newValue;
      }
      if (isDirty) {
        dirtyAttributes.pushObject(key);
      } else {
        dirtyAttributes.removeObject(key);
      }
      if (createdDirtyAttributes) {
        set(this, '_dirtyAttributes', dirtyAttributes);
      }
      return value;
    }
  }).meta({
    isAttribute: true,
    type,
    options
  });
}

// eslint-disable-next-line ember/no-classic-classes
export const ProtoBaseModel = EmberObject.extend({
  isDirty: computed('_dirtyAttributes.length', function () {
    const dirtyAttributes = this._dirtyAttributes;
    return dirtyAttributes && dirtyAttributes.length > 0 || false;
  }),
  didCreateRecord() {
    this.set('isNew', false);
  },
  didSaveRecord() {
    this.set('_dirtyAttributes', []);
  },
  getAttr(key, value) {
    return value;
  },
  dataKey(key) {
    const camelizeKeys = this.constructor.camelizeKeys;
    const meta = this.constructor.metaForProperty(key);
    if (meta.options && meta.options.key) {
      return camelizeKeys ? underscore(meta.options.key) : meta.options.key;
    }
    return camelizeKeys ? underscore(key) : key;
  },
  revert() {
    (this._dirtyAttributes === undefined ? A() : this._dirtyAttributes).clear();
    this.notifyPropertyChange('_data');
  },
  toJSON() {
    const json = {};
    const rootKey = this.constructor.rootKey;
    for (const [key, meta] of this.constructor.attributes) {
      json[this.dataKey(key)] = serialize(this[key], meta.type);
    }
    if (rootKey) {
      const jsonRoot = {};
      jsonRoot[rootKey] = json;
      return jsonRoot;
    } else {
      return json;
    }
  },
  load(hash) {
    const data = Object.assign({}, hash);
    set(this, '_data', data);
    (this._dirtyAttributes === undefined ? A([]) : this._dirtyAttributes).clear();
    set(this, 'isNew', false);
    set(this, 'isLoaded', true);
  },
  getAllProperties() {
    const json = {};
    const attributes = [...this.constructor.attributes.keys()];
    const properties = attributes ? this.getProperties(attributes) : {};
    const rootKey = this.constructor.rootKey;
    for (const key in properties) {
      const property = properties[key];
      if (isArray(property)) {
        json[key] = property.map(obj => getPropertyValue(obj));
      } else {
        json[key] = getPropertyValue(property);
      }
    }

    // TODO: support relationships

    if (rootKey) {
      const jsonRoot = {};
      jsonRoot[rootKey] = json;
      return jsonRoot;
    } else {
      return json;
    }
  }
}).reopenClass({
  create(properties) {
    const transformedProperties = {};
    if (properties) {
      const attributes = [...this.attributes.keys()];
      for (const attribute of attributes) {
        let transformedKey;
        const meta = this.metaForProperty(attribute);
        if (meta.options && meta.options.key) {
          transformedKey = this.camelizeKeys ? underscore(meta.options.key) : meta.options.key;
        } else {
          transformedKey = this.camelizeKeys ? underscore(attribute) : attribute;
        }
        transformedProperties[transformedKey] = properties[attribute];
      }
    }
    const obj = this._super();
    obj.set('isNew', true);
    obj.set('_data', transformedProperties);
    obj.setProperties(properties);
    return obj;
  },
  attributes: computed(function () {
    const map = new Map();
    this.eachComputedProperty((name, meta) => {
      if (meta.isAttribute) {
        meta.name = name; // eslint-disable-line no-param-reassign
        map.set(name, meta);
      }
    });
    return map;
  }).readOnly(),
  serialize(model) {
    return !isNone(model) ? model.toJSON() : undefined;
  },
  deserialize(data) {
    if (isNone(data)) {
      return undefined;
    }
    const record = this.create({});
    data = Object.assign({}, record._data, data); // eslint-disable-line no-param-reassign
    record.load(data);
    return record;
  },
  camelizeKeys: true
});